import type { CheckItem } from '@/content/checks'
import type { Photo } from '@/content/sections'
import { LINKS } from '@/content/pages/links'

/**
 * The Airports Industry Focus page — its copy and its figures, apart from the
 * component that draws them.
 *
 * **Why this cut has a page and fourteen others do not.** `focus()` in
 * `content/reports.ts` writes every Industry Focus entry from one sentence
 * template, because nobody had supplied the sector's own words — that is what
 * the "Still to be supplied" amber note on those entries says. Airports is the
 * fourth designed report page, asked for on 2026-08-27 — *"airpot focus daimi
 * report sayfasını bu png yi referans alarak tasarla"* — with one attachment: a
 * full-page screenshot of digitopia.co's Airports Industry Focus page. Until
 * then this report was drawn by the shared two-column template over the series
 * description.
 *
 * **The copy here was read off that screenshot, not supplied as text.** That is
 * the same position `content/daimi.ts` and `content/daimiUsa.ts` were in, and it
 * is why every band below carried a `check`: the first capture was of a 9,500px
 * page in one frame, which renders body copy at a handful of pixels, so the
 * *shape* of the page was certain and the *wording* was a careful transcription.
 * `daimiAirportsChecks()` feeds what is left into the same manifest as the rest
 * of the site's amber notes, so a production build cannot ship them unread.
 *
 * **The transcription has now been read against the original, and it was right.**
 * The page was sent back on 2026-08-28 — *"birebir tıpa tıp aynısını tasarla"* —
 * this time as four legible captures of the live page rather than one squeezed
 * into a single frame. Every paragraph, the lede and all four findings match word
 * for word, so the two notes that were waiting for exactly that reading are
 * resolved rather than carried further; the two that remain are decisions, not
 * transcriptions. Two wordings moved to the original's in the same pass: the
 * second hero paragraph's dash is unspaced there, and the figure caption carries
 * no company count.
 *
 * **The figures.** Band two carries Figure 1, the DAIMI Vitality Chart for the
 * airports industry, supplied on 2026-08-27 and shown whole. Band three wants a
 * mockup of the airports report and still has none: the file sent with the
 * 2026-08-28 captures as `airports-mockup-v1-400x326.webp` is the *Airlines*
 * mockup — same 400×326 artwork this repo already holds at
 * `photo-src/daimi-report-2026-airlines-mockup.webp`, both covers in it lettered
 * "AIRLINES FOCUS". Putting it here would show the reader a different report's
 * cover under an airports heading, so the band still runs without artwork.
 *
 * **The cover is the supplied one now.** `daimi-report-2026-airports-cover.webp`
 * was a 400px square until 2026-08-28, which is all the index card ever needed;
 * the full-size export arrived with these captures and replaced it, so the hero
 * can carry it at the same size every other Industry Focus hero does instead of
 * capping at 400px to keep it from going soft.
 *
 * **What the live page has that this page cannot.** Its five Download buttons
 * open an embedded HubSpot form. These documents are prerendered with no client
 * bundle at all (see `src/prerender.tsx`), so an embedded form script would never
 * run — and the other thirty reports on this site hand the reader the PDF from
 * our own bucket instead. So every Download button here is that PDF, and
 * `DAIMI_AP_DOWNLOAD.check` is what keeps the question of gating open.
 */

/** The report this page belongs to — the branch `pages/Reports.tsx` takes on it. */
export const DAIMI_AP_SLUG = 'digital-and-ai-maturity-report-2026-airports-industry'

/**
 * The hero's three paragraphs.
 *
 * The third is `FOCUS_CLOSE` word for word — the series' own closing line, which
 * `reports.ts` took from the live Airlines page in the first place. It is written
 * out here rather than imported so this module reads as the page reads, and so
 * that rewording the series line for the other cuts cannot silently reword a page
 * whose copy is the sector's own.
 */
export const DAIMI_AP_INTRO = {
  body: [
    'Airports operate at a high-stakes intersection of security, infrastructure, and passenger experience, where disruptions are immediate and highly visible. While operational foundations are solid, “legacy gravity” and multi-stakeholder fragmentation currently limit maturity.',
    'To lead in 2026, airports must shift toward real-time orchestration, treating the facility as a single integrated system.',
    'This report outlines the sector’s maturity, leading strategies, and actions to accelerate transformation.',
  ],
  /**
   * The report cover, which `content/reports.ts` has also been showing on the
   * index card since the covers were supplied on 2026-08-26.
   *
   * That file was a 400px square, which is why the hero used to cap this column
   * at 400px — anything wider showed soft. The full-size export arrived on
   * 2026-08-28 and replaced it under the same name, so the index card is
   * unchanged and the hero is at the size the other Industry Focus heroes use.
   */
  photo: {
    src: '/photo/daimi-report-2026-airports-cover.webp',
    alt: 'Digital & AI Maturity Report 2026: Airports Focus, February 2026',
  } satisfies Photo,
  /*
   * No amber note here any more. It asked for these paragraphs to be read against
   * digitopia.co once, because they were transcribed off a capture too small to
   * read; the legible captures arrived on 2026-08-28 and all three match word for
   * word. That matters twice over, since `content/reports.ts` takes this page's
   * search-result description from the first of them.
   */
}

/**
 * The findings, and Figure 1 beside them.
 *
 * The four bullets are one block on the live page in which each line opens with
 * a bolded label — "Core Strengths:", "The Scaling Gap:", "Industrializing AI:",
 * "Endurance Advantage:". Split into `label` and `body` here so the label can be
 * set rather than emphasised inside a sentence, which is what a `<strong>` in the
 * middle of a paragraph gives.
 */
export const DAIMI_AP_FINDINGS = {
  heading: 'DAIMI Findings: Ecosystem Dependency vs. Fragmentation',
  lede: 'The 2026 benchmark shows a maturity pattern where agility (Velocity) and foundational reliability (Endurance) rise together, though most airports remain clustered around mid-level performance.',
  points: [
    {
      label: 'Core Strengths',
      body: 'Maturity is strongest in operational discipline and passenger experience, particularly where digital touchpoints like self-service and wayfinding reduce friction.',
    },
    {
      label: 'The Scaling Gap',
      body: 'Progress is frequently hindered by ecosystem dependency, as improvement requires shared data and aligned incentives across airlines, security, and ground operations.',
    },
    {
      label: 'Industrializing AI',
      body: 'While interest is high for flow prediction and gate optimization, execution often weakens when pilots fail to become operational standards due to procurement complexity.',
    },
    {
      label: 'Endurance Advantage',
      body: 'Strong endurance traits are supported by stringent governance, safety, and reliability requirements inherent to the sector.',
    },
  ],
  /**
   * Figure 1 — the supplied vitality chart, shown whole.
   *
   * This band went through three states in two days, which is worth recording
   * because the last one is the only honest one. It began as a card of type — a
   * big "45" and the two axes described in sentences — because no export of the
   * plot had been sent. Asked for again on 2026-08-27 against the reference
   * screenshot (*"chart böyle ama senin yaptığın kötü gözüküyor"*), so the page
   * drew the scatter itself in inline SVG, with the cloud traced off a full-page
   * capture. Then the export arrived, same afternoon: 1200×1185 on a white ground,
   * the same file type the Airlines and Construction cuts came in.
   *
   * So the drawn `VitalityChart` in `pages/DaimiAirports.tsx` is gone and this is
   * the real plot. A traced cloud is a decent likeness and still not the data; the
   * moment the data exists, showing the likeness instead is a choice nobody would
   * defend.
   *
   * It is white-on-white against `--band`, so the page gives it a white card and a
   * 640px floor with the frame scrolling under it — forty-five marks inside one
   * unit of each axis do not survive being squeezed to phone width.
   */
  photo: {
    src: '/photo/daimi-airports-vitality-chart.webp',
    alt: 'DAIMI Vitality Chart for the airports industry: 45 airports plotted on Digital Velocity against Digital Endurance, in one cluster between 2.4 and 3.6 on velocity and 2.7 and 3.5 on endurance, tilted so the two rise together',
  } satisfies Photo,
  /**
   * The live caption, word for word — it names no company count, and the chart
   * above it already prints "Company Count 45" in its own furniture. This page
   * had put the figure in the caption too, back when the note below was asking
   * for the export; carrying it twice is the kind of small divergence a "birebir"
   * request is about.
   */
  caption: 'Figure 1: DAIMI Vitality Chart – Airports Industry',
  expertLabel: 'Talk to an expert',
  expertHref: LINKS.contact,
  /*
   * No amber note here either. It was open on the wording — the lede and the four
   * findings were transcribed from a capture where body copy rendered a few pixels
   * tall — and the legible captures of 2026-08-28 match all five exactly. The
   * figure itself was settled a day earlier: the supplied export states the
   * company count of 45, which is also the number the Airlines cut reports.
   */
}

/** The analysis band. */
export const DAIMI_AP_ANALYSIS = {
  heading: 'A Comprehensive Analysis of Digital & AI Maturity in Airports Industry',
  body: [
    'To lead in 2026, airport executives must shift from reactive operations to real-time orchestration. Success requires investing in a unified operational data layer that connects stakeholders and simplifies core systems to enable automation. By upskilling operational teams and fostering data interoperability with partners, airports can turn isolated excellence into a compounding ecosystem advantage.',
    'Download the full report to access the detailed DAIMI benchmarks and the strategic roadmap to turn infrastructure pressure into a sustained, AI-driven operational edge.',
  ],
  check:
    'This band still has no mockup, and the file sent for it on 2026-08-28 cannot be the one. It came named “airports-mockup-v1-400x326.webp”, but the artwork is the Airlines mockup — both covers in it are lettered “AIRLINES FOCUS”, and it is the same 400×326 image this site already shows on the Airlines Focus page. Under this heading it would tell the reader they are looking at a different report, so the band runs without artwork rather than with the wrong cover. Two ways out: send the airports mockup, or say that digitopia.co’s own airports page is showing the airlines artwork and should be corrected there too.',
}

/*
 * The methodology and global-perspective bands used to be written out here, each
 * with its own transcription of copy that is the same on every focus cut. They
 * are `content/reportClosing.ts` now, drawn by `components/ReportClosing.tsx` on
 * every report page but three — asked for on 2026-08-28, *"her report sayfamızın
 * en altında bu tasarım bire bir aynı şekilde olmalı"*. The wheel's amber note
 * went with them: it asked for an export lettered in white so the drawing could
 * come off its light plate, and that export arrived with the same request.
 */

/** The closing band, which is where the live page's gated form sits. */
export const DAIMI_AP_DOWNLOAD = {
  heading: 'Download Report',
  body: 'Get the Airports Industry Focus cut of the Digital & AI Maturity Report 2026.',
  /**
   * Resolved 2026-08-30. This band asked whether the report should sit behind
   * the form the live page gates it with, because every Download button here
   * linked straight to the bucket. It now does: the buttons carry
   * `data-gated-download` (`lib/gatedDownload.ts`), so the reader is asked for
   * an email at the foot of this page and `website-form-lead` sends the PDF to
   * it. There is no direct link left on the page to gate.
   */
  check: undefined,
}

/**
 * The page's open questions, in the shape `build-manifest.json` records copy
 * checks in — the same mechanism `daimiChecks()` and `daimiConsumerGoodsChecks()`
 * use, and appended to the same list in `reportChecks()`.
 *
 * Two left, down from four. The transcription notes on the hero and the findings
 * were answered on 2026-08-28 by captures legible enough to read the page against,
 * and every word matched. What remains is a missing file and a decision: the
 * airports mockup, and whether the download should be gated. Both stay
 * `'content'` rather than `'asset'` — the mockup question is not "a file is
 * missing" but "the file we were sent is a different report's cover", which is a
 * conversation rather than an upload.
 */
export function daimiAirportsChecks(): CheckItem[] {
  const sections: { section: string; check?: string }[] = [
    { section: 'analysis', check: DAIMI_AP_ANALYSIS.check },
    { section: 'download', check: DAIMI_AP_DOWNLOAD.check },
  ]
  return sections
    .filter((s): s is { section: string; check: string } => Boolean(s.check))
    .map((s) => ({
      locale: 'en',
      page: `impact-labs/reports/${DAIMI_AP_SLUG}`,
      section: s.section,
      text: s.check,
      severity: 'content',
    }))
}
